import { collection, getDocs, doc, getDoc, query, where, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Product, WebshopProduct, ProductFilter, ProductSort, ProductCategory, InventoryItem, CategoryWithSubcategories } from '../types';
import { getProductFilter, getInventoryFilter } from '../config/webshopConfig';

// Termék név tisztítása
const cleanProductName = (name: string): string => {
  return name
    .replace(/^TESZT\s*/i, '') // TESZT előtag eltávolítása
    .replace(/\s*\d+\s*db\s*$/i, '') // Készlet információ eltávolítása a végéről
    .replace(/\s*Használt\s*\d+\s*db\s*/gi, '') // "Használt X db" eltávolítása
    .trim();
};

// Leírás tisztítása - hibabiztos
const cleanDescription = (description: string | undefined): string => {
  if (!description || typeof description !== 'string') return '';
  return description
    .replace(/^TESZT\s*/i, '') // TESZT előtag eltávolítása
    .trim();
};

// Segédfüggvények
const transformProductForWebshop = (product: Product): WebshopProduct => {
  return {
    id: product.id,
    name: cleanProductName(product.name),
    description: cleanDescription(product.description),
    price: product.grossPrice || 0,
    originalPrice: product.netPrice || 0,
    imageUrl: product.imageUrl || product.images?.[0]?.url || '/placeholder-product.jpg',
    category: product.category?.name || 'Egyéb',
    categoryId: product.category?.id || '',
    inStock: true, // Dinamikusan frissülő érték a szűrés alapján
    stockLevel: 0, // Nem használjuk a Products oldalon
    rating: 0, // Nem használjuk
    reviewCount: 0, // Nem használjuk
    isNew: false, // Alapértelmezett érték - később kiterjeszthető
    isSale: false, // Alapértelmezett érték - később kiterjeszthető
    isFeatured: false, // Alapértelmezett érték - később kiterjeszthető
    isOutlet: false, // Alapértelmezett érték - később kiterjeszthető
    code: product.code,
    unit: product.unit,
    taxRate: product.taxRate || 27,
    warrantyDays: product.warrantyDays,
    manufacturer: product.manufacturer,
    createdAt: product.createdAt,
    updatedAt: product.updatedAt
  };
};

// Firebase Timestamp konvertálás segédfüggvény
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date();
};

class ProductService {
  private categoriesCache: CategoryWithSubcategories[] | null = null;
  private categoriesCacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 perc cache

  // Termékek lekérése webshop specifikus szűréssel
  async getProducts(page = 1, pageSize = 20, categoryId?: string, searchTerm?: string): Promise<{
    products: Product[];
    totalItems: number;
    hasNextPage: boolean;
  }> {
    try {
      const filter = getProductFilter();
      
      // Alap query termékekhez
      let productsQuery = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        where('webStore', '==', true),
        orderBy('name'),
        limit(pageSize)
      );

      // Kategória szűrés
      if (categoryId) {
        productsQuery = query(
          collection(db, 'products'),
          where('isActive', '==', true),
          where('webStore', '==', true),
          where('category.id', '==', categoryId),
          orderBy('name'),
          limit(pageSize)
        );
      }

      const productsSnapshot = await getDocs(productsQuery);
      const products: Product[] = [];

      for (const doc of productsSnapshot.docs) {
        const productData = doc.data() as Product;
        const product: Product = {
          ...productData,
          id: doc.id,
          createdAt: convertTimestamp(productData.createdAt),
          updatedAt: convertTimestamp(productData.updatedAt)
        };

        // Szervezet specifikus ár és adatok hozzáadása
        const orgProduct = product.organizationProducts?.find(
          op => op.organizationId === filter.organizationId
        );

        if (orgProduct) {
          product.netPrice = Number(orgProduct.price);
          product.taxRate = Number(orgProduct.taxRate) as any;
          product.grossPrice = Number(orgProduct.price) * (1 + (Number(orgProduct.taxRate) / 100));
        }

        // Alapértelmezett készlet érték beállítása
        product.currentStock = product.currentStock || 0;

        products.push(product);
      }

      // Keresési szűrés (ha van)
      let filteredProducts = products;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredProducts = products.filter(product =>
          product.name.toLowerCase().includes(searchLower) ||
          product.code.toLowerCase().includes(searchLower) ||
          product.description.toLowerCase().includes(searchLower)
        );
      }

      return {
        products: filteredProducts,
        totalItems: filteredProducts.length,
        hasNextPage: filteredProducts.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('Failed to fetch products');
    }
  }

  // Egy termék lekérése ID alapján
  async getProductById(productId: string): Promise<Product | null> {
    try {
      const filter = getProductFilter();
      const productDoc = await getDoc(doc(db, 'products', productId));
      
      if (!productDoc.exists()) {
        return null;
      }

      const productData = productDoc.data() as Product;
      const product: Product = {
        ...productData,
        id: productDoc.id,
        createdAt: convertTimestamp(productData.createdAt),
        updatedAt: convertTimestamp(productData.updatedAt)
      };

      // Szervezet specifikus ár és adatok hozzáadása
      const orgProduct = product.organizationProducts?.find(
        op => op.organizationId === filter.organizationId
      );

      if (orgProduct) {
        product.netPrice = Number(orgProduct.price);
        product.taxRate = Number(orgProduct.taxRate) as any;
        product.grossPrice = Number(orgProduct.price) * (1 + (Number(orgProduct.taxRate) / 100));
      }

      // Alapértelmezett készlet érték beállítása
      product.currentStock = product.currentStock || 0;

      return product;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw new Error('Failed to fetch product');
    }
  }

  // Kategóriák lekérése hierarchikus struktúrával (cache-szel)
  async getCategories(): Promise<CategoryWithSubcategories[]> {
    // Ellenőrizzük a cache-t
    const now = Date.now();
    if (this.categoriesCache && (now - this.categoriesCacheTime) < this.CACHE_DURATION) {
      console.log('Kategóriák betöltve cache-ből');
      return this.categoriesCache;
    }

    try {
      console.log('Kategóriák betöltése Firebase-ből...');
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const categoriesSnapshot = await getDocs(categoriesQuery);
      const allCategories: ProductCategory[] = [];

      categoriesSnapshot.forEach(doc => {
        const categoryData = doc.data() as ProductCategory;
        allCategories.push({
          ...categoryData,
          id: doc.id,
          createdAt: convertTimestamp(categoryData.createdAt),
          updatedAt: convertTimestamp(categoryData.updatedAt)
        });
      });

      // Hierarchikus struktúra építése
      const mainCategories: Record<string, CategoryWithSubcategories> = {};

      // Először összegyűjtjük a főkategóriákat
      allCategories
        .filter(cat => !cat.parentId || cat.parentId === null)
        .forEach(cat => {
          mainCategories[cat.id] = {
            ...cat,
            subcategories: []
          };
        });

      // Majd hozzáadjuk az alkategóriákat a szüleikhez
      allCategories
        .filter(cat => cat.parentId && cat.parentId !== null)
        .forEach(cat => {
          if (mainCategories[cat.parentId!]) {
            mainCategories[cat.parentId!].subcategories?.push(cat);
          }
        });

      // Tömbbe konvertálás és rendezés
      const mainCategoriesArray = Object.values(mainCategories).sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      // Alkategóriák rendezése
      mainCategoriesArray.forEach(main => {
        if (main.subcategories) {
          main.subcategories.sort((a, b) => a.name.localeCompare(b.name));
        }
      });

      // Cache-be mentés
      this.categoriesCache = mainCategoriesArray;
      this.categoriesCacheTime = now;

      console.log(`${mainCategoriesArray.length} főkategória és ${allCategories.length - mainCategoriesArray.length} alkategória betöltve Firebase-ből`);
      return mainCategoriesArray;
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Visszaadunk egy alapértelmezett kategóriát, ha hiba van
      return [
        {
          id: 'default',
          name: 'Egyéb',
          parentId: undefined,
          fullPath: 'Egyéb',
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          subcategories: []
        }
      ];
    }
  }

  // Cache törlése (opcionális, ha szükséges frissíteni a kategóriákat)
  clearCategoriesCache(): void {
    this.categoriesCache = null;
    this.categoriesCacheTime = 0;
    console.log('Kategóriák cache törölve');
  }

  // Termék készlet adatok lekérése webshop specifikusan
  async getProductInventory(productId: string): Promise<{
    totalAvailable: number;
    totalQuantity: number;
    warehouses: Array<{
      warehouseId: string;
      warehouseName: string;
      warehouseAddress: string;
      available: number;
      quantity: number;
    }>;
  }> {
    try {
      const filter = getInventoryFilter();
      
      // Először lekérjük az online engedélyezett raktárakat
      const warehousesQuery = query(
        collection(db, 'warehouses'),
        where('isActive', '==', true),
        where('allowOnlineOrders', '==', true)
      );
      const warehousesSnapshot = await getDocs(warehousesQuery);
      const allowedWarehouseIds = warehousesSnapshot.docs.map(doc => doc.id);
      
      // Csak az online engedélyezett raktárak használata
      const finalWarehouseIds = allowedWarehouseIds;

      if (finalWarehouseIds.length === 0) {
        console.warn('No allowed warehouses found for online orders');
        return {
          totalAvailable: 0,
          totalQuantity: 0,
          warehouses: []
        };
      }

      // Készlet lekérés csak az engedélyezett raktárakból
      const inventoryQuery = query(
        collection(db, 'inventory'),
        where('productId', '==', productId),
        where('warehouseId', 'in', finalWarehouseIds)
      );

      const inventorySnapshot = await getDocs(inventoryQuery);
      let totalAvailable = 0;
      let totalQuantity = 0;
      const warehouses: Array<{
        warehouseId: string;
        warehouseName: string;
        warehouseAddress: string;
        available: number;
        quantity: number;
      }> = [];

      // Warehouse adatok mapping létrehozása (név és cím)
      const warehouseMap = new Map();
      warehousesSnapshot.docs.forEach(doc => {
        const warehouseData = doc.data();
        warehouseMap.set(doc.id, {
          name: warehouseData.name,
          address: warehouseData.location || warehouseData.address || ''
        });
        console.log(`Allowed warehouse: ${doc.id} - ${warehouseData.name}, location: ${warehouseData.location} (allowOnlineOrders: ${warehouseData.allowOnlineOrders})`);
      });

      inventorySnapshot.forEach(doc => {
        const inventoryData = doc.data() as InventoryItem;
        
        // Biztonságos számok használata NaN megelőzésére
        const quantity = typeof inventoryData.quantity === 'number' && !isNaN(inventoryData.quantity) ? inventoryData.quantity : 0;
        const reservedQuantity = typeof inventoryData.reservedQuantity === 'number' && !isNaN(inventoryData.reservedQuantity) ? inventoryData.reservedQuantity : 0;
        const available = Math.max(0, quantity - reservedQuantity);
        
        const warehouseInfo = warehouseMap.get(inventoryData.warehouseId);
        
        console.log(`Inventory item for ${productId}:`, {
          warehouseId: inventoryData.warehouseId,
          warehouseName: warehouseInfo?.name || 'Ismeretlen raktár',
          warehouseAddress: warehouseInfo?.address || '',
          originalQuantity: inventoryData.quantity,
          originalReserved: inventoryData.reservedQuantity,
          cleanQuantity: quantity,
          cleanReserved: reservedQuantity,
          available
        });
        
        totalAvailable += available;
        totalQuantity += quantity;

        warehouses.push({
          warehouseId: inventoryData.warehouseId,
          warehouseName: warehouseInfo?.name || inventoryData.warehouseName || 'Ismeretlen raktár',
          warehouseAddress: warehouseInfo?.address || '',
          available,
          quantity: quantity
        });
      });

      console.log(`Inventory check for product ${productId}:`, {
        allowedWarehouses: finalWarehouseIds.length,
        totalAvailable,
        warehouses: warehouses.length
      });

      return {
        totalAvailable,
        totalQuantity,
        warehouses
      };
    } catch (error) {
      console.error('Error fetching product inventory:', error);
      return {
        totalAvailable: 0,
        totalQuantity: 0,
        warehouses: []
      };
    }
  }

  // Keresési javaslatok
  async getSearchSuggestions(searchTerm: string): Promise<string[]> {
    try {
      const productsQuery = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        where('webStore', '==', true),
        orderBy('name'),
        limit(10)
      );

      const productsSnapshot = await getDocs(productsQuery);
      const suggestions: string[] = [];
      const searchLower = searchTerm.toLowerCase();

      productsSnapshot.forEach(doc => {
        const productData = doc.data() as Product;
        
        if (productData.name.toLowerCase().includes(searchLower)) {
          suggestions.push(productData.name);
        }
        
        if (productData.code.toLowerCase().includes(searchLower)) {
          suggestions.push(productData.code);
        }
      });

      // Duplikációk eltávolítása és max 5 javaslat
      const uniqueSuggestions = Array.from(new Set(suggestions));
      return uniqueSuggestions.slice(0, 5);
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      return [];
    }
  }

  // Kiemelt termékek
  async getFeaturedProducts(limitCount = 8): Promise<Product[]> {
    try {
      const filter = getProductFilter();
      
      const productsQuery = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        where('webStore', '==', true),
        orderBy('name', 'asc'),
        limit(limitCount)
      );

      const productsSnapshot = await getDocs(productsQuery);
      const products: Product[] = [];

      for (const doc of productsSnapshot.docs) {
        const productData = doc.data() as Product;
        const product: Product = {
          ...productData,
          id: doc.id,
          createdAt: convertTimestamp(productData.createdAt),
          updatedAt: convertTimestamp(productData.updatedAt)
        };

        // Szervezet specifikus ár hozzáadása
        const orgProduct = product.organizationProducts?.find(
          op => op.organizationId === filter.organizationId
        );

        if (orgProduct) {
          product.netPrice = Number(orgProduct.price);
          product.taxRate = Number(orgProduct.taxRate) as any;
          product.grossPrice = Number(orgProduct.price) * (1 + (Number(orgProduct.taxRate) / 100));
        } else {
          // Ha nincs szervezet specifikus ár, használjuk az alapértelmezett árakat
          product.netPrice = product.netPrice || 0;
          product.grossPrice = product.grossPrice || product.netPrice || 0;
          product.taxRate = product.taxRate || 27;
        }

        // Alapértelmezett készlet érték beállítása
        product.currentStock = product.currentStock || 0;

        products.push(product);
      }

      return products;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      return [];
    }
  }

  // Valós készlet ellenőrzése engedélyezett raktárakból
  private async checkRealStock(productId: string): Promise<number> {
    try {
      // Engedélyezett raktárak lekérése
      const warehousesQuery = query(
        collection(db, 'warehouses'),
        where('isActive', '==', true),
        where('allowOnlineOrders', '==', true)
      );
      const warehousesSnapshot = await getDocs(warehousesQuery);
      const allowedWarehouseIds = warehousesSnapshot.docs.map(doc => doc.id);

      if (allowedWarehouseIds.length === 0) return 0;

      // Készlet lekérés csak az engedélyezett raktárakból
      const inventoryQuery = query(
        collection(db, 'inventory'),
        where('productId', '==', productId),
        where('warehouseId', 'in', allowedWarehouseIds)
      );

      const inventorySnapshot = await getDocs(inventoryQuery);
      let totalAvailable = 0;

      inventorySnapshot.forEach(doc => {
        const inventoryData = doc.data();
        const quantity = typeof inventoryData.quantity === 'number' && !isNaN(inventoryData.quantity) ? inventoryData.quantity : 0;
        const reservedQuantity = typeof inventoryData.reservedQuantity === 'number' && !isNaN(inventoryData.reservedQuantity) ? inventoryData.reservedQuantity : 0;
        const available = Math.max(0, quantity - reservedQuantity);
        totalAvailable += available;
      });

      return totalAvailable;
    } catch (error) {
      console.error('Error checking real stock:', error);
      return 0;
    }
  }

  // Webáruházban megjelenő termékek lekérése - CRM stílusú szűréssel
  async getWebshopProducts(filter?: ProductFilter, sort?: ProductSort, pageSize: number = 25, lastDoc?: any): Promise<{ products: WebshopProduct[], lastDoc: any, hasMore: boolean }> {
    try {
      const filterConfig = getProductFilter();
      
      // Összes termék lekérése (mint a CRM-ben)
      const productsQuery = query(
        collection(db, 'products'),
        where('webStore', '==', true),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );

      const querySnapshot = await getDocs(productsQuery);
      
      // Termékek feldolgozása
      const allProducts: Product[] = [];
      
      for (const doc of querySnapshot.docs) {
        const productData = doc.data() as Product;
        const product: Product = {
          ...productData,
          id: doc.id,
          createdAt: convertTimestamp(productData.createdAt),
          updatedAt: convertTimestamp(productData.updatedAt)
        };

        // Szervezet specifikus ár hozzáadása
        const orgProduct = product.organizationProducts?.find(
          op => op.organizationId === filterConfig.organizationId
        );

        if (orgProduct) {
          product.netPrice = Number(orgProduct.price);
          product.taxRate = Number(orgProduct.taxRate) as any;
          product.grossPrice = Number(orgProduct.price) * (1 + (Number(orgProduct.taxRate) / 100));
        } else {
          // Ha nincs szervezet specifikus ár, használjuk az alapértelmezett árakat
          product.netPrice = product.netPrice || 0;
          product.grossPrice = product.grossPrice || product.netPrice || 0;
          product.taxRate = product.taxRate || 27;
        }

        // Alapértelmezett készlet érték beállítása
        product.currentStock = product.currentStock || 0;

        allProducts.push(product);
      }

      // Kliens oldali szűrés (mint a CRM-ben) - először a gyors szűrők
      let filteredProducts = allProducts.filter(product => {
        // Keresési szűrés
        if (filter?.searchTerm) {
          const term = filter.searchTerm.toLowerCase();
          const matchesSearch = 
            (product.name || '').toLowerCase().includes(term) ||
            (product.code || '').toLowerCase().includes(term) ||
            (product.description || '').toLowerCase().includes(term);
          if (!matchesSearch) return false;
        }

        // Kategória szűrés - level1, level2, level3, ID és slug alapján
        if (filter?.category) {
          const category = product.category as any;
          
          // Támogatjuk a vesszővel elválasztott kategóriákat (főkategória + alkategóriák)
          const categoriesToMatch = filter.category.split(',');
          
          // Debug log
          console.log('🔍 Kategória szűrés:', {
            filterCategories: categoriesToMatch,
            productName: product.name,
            categoryData: {
              id: product.category?.id,
              categoryId: category?.categoryId,
              subCategoryId: category?.subCategoryId,
              slug: category?.slug,
              level1: category?.level1,
              level2: category?.level2,
              level3: category?.level3,
              fullPath: category?.fullPath
            }
          });
          
          const matchesCategory = categoriesToMatch.some(filterCat => 
            // ID alapú egyezés (CRM categoryId-t használ)
            product.category?.id === filterCat ||
            category?.categoryId === filterCat ||
            category?.subCategoryId === filterCat ||
            // Slug alapú egyezés
            category?.slug === filterCat ||
            // Level alapú egyezés (CRM struktúra)
            category?.level1 === filterCat ||
            category?.level2 === filterCat ||
            category?.level3 === filterCat ||
            // fullPath alapú egyezés (részleges is)
            category?.fullPath?.includes(filterCat) ||
            // Normázás nélküli egyezés
            category?.level1?.toLowerCase().replace(/\s+/g, '_') === filterCat.toLowerCase() ||
            category?.level2?.toLowerCase().replace(/\s+/g, '_') === filterCat.toLowerCase() ||
            category?.level3?.toLowerCase().replace(/\s+/g, '_') === filterCat.toLowerCase()
          );
          
          console.log('✅ Egyezés:', matchesCategory);
          
          if (!matchesCategory) return false;
        }

        // Ár szűrés
        if (filter?.minPrice !== undefined && (product.netPrice || 0) < filter.minPrice) {
          return false;
        }

        if (filter?.maxPrice !== undefined && (product.netPrice || 0) > filter.maxPrice) {
          return false;
        }

        return true;
      });

      // Készlet szűrés külön - valós raktári készlet alapján
      if (filter?.inStock) {
        const stockCheckedProducts = [];
        
        for (const product of filteredProducts) {
          const realStock = await this.checkRealStock(product.id);
          if (realStock > 0) {
            stockCheckedProducts.push(product);
          }
        }
        
        filteredProducts = stockCheckedProducts;
      }

      // Rendezés
      if (sort) {
        filteredProducts.sort((a, b) => {
          switch (sort.field) {
            case 'price':
              return sort.direction === 'asc' 
                ? (a.netPrice || 0) - (b.netPrice || 0)
                : (b.netPrice || 0) - (a.netPrice || 0);
            case 'name':
              return sort.direction === 'asc' 
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name);
            case 'createdAt':
              return sort.direction === 'asc' 
                ? a.createdAt.getTime() - b.createdAt.getTime()
                : b.createdAt.getTime() - a.createdAt.getTime();
            default:
              return 0;
          }
        });
      }

      // Lapozás
      const startIndex = lastDoc ? parseInt(lastDoc) : 0;
      const endIndex = startIndex + pageSize;
      const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

      // Webshop termékekre konvertálás
      const webshopProducts = paginatedProducts.map(transformProductForWebshop);
      
      return {
        products: webshopProducts,
        lastDoc: endIndex.toString(),
        hasMore: endIndex < filteredProducts.length
      };
    } catch (error) {
      console.error('Error fetching webshop products:', error);
      return { products: [], lastDoc: null, hasMore: false };
    }
  }

  // Kategória alapján termékek lekérése
  async getProductsByCategory(categoryId: string, pageSize: number = 20): Promise<WebshopProduct[]> {
    try {
      const filterConfig = getProductFilter();
      
      // Összes termék lekérése (mint a CRM-ben)
      const productsQuery = query(
        collection(db, 'products'),
        where('webStore', '==', true),
        where('isActive', '==', true),
        where('discontinued', '!=', true),
        orderBy('name', 'asc')
      );

      const querySnapshot = await getDocs(productsQuery);
      
      // Termékek feldolgozása és kategória szűrés
      const products: Product[] = [];
      
      for (const doc of querySnapshot.docs) {
        const productData = doc.data() as Product;
        const product: Product = {
          ...productData,
          id: doc.id,
          createdAt: convertTimestamp(productData.createdAt),
          updatedAt: convertTimestamp(productData.updatedAt)
        };

        // Szervezet specifikus ár hozzáadása
        const orgProduct = product.organizationProducts?.find(
          op => op.organizationId === filterConfig.organizationId
        );

        if (orgProduct) {
          product.netPrice = Number(orgProduct.price);
          product.taxRate = Number(orgProduct.taxRate) as any;
          product.grossPrice = Number(orgProduct.price) * (1 + (Number(orgProduct.taxRate) / 100));
        } else {
          // Ha nincs szervezet specifikus ár, használjuk az alapértelmezett árakat
          product.netPrice = product.netPrice || 0;
          product.grossPrice = product.grossPrice || product.netPrice || 0;
          product.taxRate = product.taxRate || 27;
        }

        // Alapértelmezett készlet érték beállítása
        product.currentStock = product.currentStock || 0;

        // Kategória szűrés - CRM stílusú
        const matchesCategory = 
          product.category?.categoryId === categoryId ||
          product.category?.subCategoryId === categoryId ||
          (product.category as any)?.id === categoryId;

        if (matchesCategory) {
          products.push(product);
        }
      }

      // Lapozás
      const limitedProducts = products.slice(0, pageSize);

      return limitedProducts.map(transformProductForWebshop);
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  }
}

export const productService = new ProductService(); 